<template>
    <div class="container">
        <v-title :lineage="lineage">
            {{isFormUpdating ? 'Update' : 'Add'}} {{item.type | capitalize}}
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages[previousRoute.name].isAllowed"
                    class="btn inline-s" :to="previousRoute">
                    Cancel
                </router-link>
                <r-button :loading="isLoading" type="primary" @click="save">Save {{item.type}}</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="hasTriggeredErrors"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.item.name.required">Please provide a name for this rule.</li>
            <template v-if="validationAggregator.aggregatedResult">
                <li class="list-item" v-for="error in validationAggregator.aggregatedResult">{{error.name}}</li>
            </template>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">{{item.type | capitalize}} Info</h2>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                :label="item.type.concat(' Name') | capitalize"
                                auto-focus
                                :help-text="nameHelpText"
                                :validate="$v.item.name"
                                v-model="item.name"/>
                        </div>
                    </div>
                </div>
                <template v-if="!item.isBind">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div class="grid-item">
                                <v-tag-input
                                    label="Labels"
                                    :help-text="labelHelpText"
                                    :tags="existingLabels"
                                    v-model="item.labels"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content">
                        <div class="grid-content grid-unstackable">
                            <div class="grid-item">
                                <v-checkbox
                                    :value="true"
                                    v-model="item.final"
                                    label="Stop Further Processing"
                                    caption="Enable to stop further processing of rules that follow this one in the event's list."/>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Conditions</h2>
            <template slot="description">
                <p :class="{'text-muted': true, 'stack-s': isReminderEvent}">
                    Define conditions to filter when this {{item.type}} should run. If the conditions
                    do not match for this event then the next {{item.type}} in line will be processed.
                </p>
                <p class="text-muted" v-if="isReminderEvent">
                    Tip: set a condition on the {{reminderNumberProperty}} to overwrite a specific reminder.
                </p>
            </template>
            <div slot="content">
                <condition-builder
                    v-if="isDefinitionReady"
                    :criteria="item.criteria"
                    :event="systemEvent"
                    @update="updateCriteria"
                />
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Actions</h2>
            <p class="text-muted" slot="description">Add actions to trigger when the conditions for this
                {{item.type}} match. All actions will run unless they are toggled off.</p>
            <actions-list
                v-if="isDefinitionReady"
                :is-form-updating="isFormUpdating"
                :validation-aggregator="validationAggregator"
                :actions="item.actions"
                :event="systemEvent"
                @add-action="addAction"
                @toggle-action-status="toggleActionStatus"
                @delete-action="deleteAction"
                @update-action="updateAction"
                :pre-processor="actionPreProcessor"
                :new-action-predicate="isNewlyAddedAction"
                slot="content"/>
        </v-form-tile>
        <div class="divider"></div>
        <v-error-messages
            :has-frontend-errors="hasTriggeredErrors"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.item.name.required">Please provide a name for this rule.</li>
            <template v-if="validationAggregator.aggregatedResult">
                <li class="list-item" v-for="error in validationAggregator.aggregatedResult">{{error.name}}</li>
            </template>
        </v-error-messages>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <router-link
                    v-if="$auth.$pages[previousRoute.name].isAllowed"
                    class="btn inline-s" :to="previousRoute">
                    Cancel
                </router-link>
                <r-button :loading="isLoading" type="primary" @click="save">Save {{item.type}}</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import EventModel from '@/common/models/rules/event-model';
    import RuleFactory from '@/common/models/rules/rule-factory';
    import vTitle from '@/components/v-title';
    import {
        vCheckbox,
        vErrorMessages,
        vFormTile,
        vTagInput,
    } from '@/components/form';
    import {required} from 'vuelidate/lib/validators';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import ValidationAggregator from '@/common/validation/validation-aggregator';
    import Base58 from '@/common/helpers/base58';
    import RuleSetModel from '@/common/models/rules/rule-set-model';
    import RuleModel from '@/common/models/rules/rule-model';
    import _ from '@/common/helpers';
    import actionPreProcessor from '@/modules/rules-engine/components/rule-form/action-pre-processor';
    import ReminderActionModel from '@/common/models/rules/actions/reminder-action-model';
    import EventTypes from '@/common/api/definitions/event-types';

    export default {
        name: 'rule-form',
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vFormTile,
            vErrorMessages,
            vCheckbox,
            vTagInput,
            ActionsList: () => import(/* webpackChunkName: "rules-engine" */ '@/modules/rules-engine/components/rule-form/actions-list'),
            ConditionBuilder: () => import(/* webpackChunkName: "rules-engine" */ '@/modules/rules-engine/components/condition-builder/condition-builder'),
        },
        props: {
            eventType: String,
            eventId: String,
            itemIndex: String,
        },
        computed: {
            predicate() {
                return this.itemIndex !== undefined;
            },
            lineage() {
                return [
                    {
                        [`${_.unKebabCase(this.eventType)} Event`]: this.previousRoute,
                    },
                ];
            },
            nameHelpText() {
                return `Use a unique name to help differentiate between multiple ${this.item.type}s.`;
            },
            labelHelpText() {
                return `Labels can be used to categorize your ${this.item.type}s and filter them. Enter values and press enter to add them as a label.`;
            },
            previousRoute() {
                return {
                    name: 'view-system-event',
                    params: {
                        eventType: this.eventType,
                        view: this.item.family,
                    },
                };
            },
            systemEvent() {
                return new EventModel({eventType: this.eventType});
            },
            ruleIndex() {
                return Base58.decode(this.itemIndex);
            },
            itemLabels: {
                get() {
                    return (this.item.labels || []).join(', ');
                },
                set(value) {
                    this.item.labels = value
                        .split(',')
                        .map(label => label.trim());
                },
            },
            existingLabels() {
                const labels = this.$store.getters['rulesEngine/labels'];
                if (labels.length) {
                    return labels.reduce((result, label) => {
                        result[label] = label;
                        return result;
                    }, {});
                }
                return {};
            },
            hasTriggeredErrors() {
                return this.$v.item.$error || (!!(this.validationAggregator.aggregatedResult && this.validationAggregator.aggregatedResult.length));
            },
            isDataRequired() {
                // Fetches data even for new rule.
                return true;
            },
            isReminderEvent() {
                return ReminderActionModel.ReminderEvents.includes(this.eventType);
            },
            reminderNumberProperty() {
                switch (this.eventType) {
                    case EventTypes.subscriptionTrialEndReminder:
                        return 'Subscription\'s trial reminder number';
                    case EventTypes.subscriptionRenewalReminder:
                        return 'Subscription\'s renewal reminder number';
                    case EventTypes.invoicePastDueReminder:
                        return 'Invoice\'s due reminder number';
                    case EventTypes.paymentCardExpirationReminder:
                        return 'Payment Card\'s expiration reminder number';
                    default:
                        return '...';
                }
            },
        },
        data() {
            return {
                // TODO: Consider to rename to rule.
                item: RuleFactory.create(this.$route.meta.itemType),
                ruleSet: new RuleSetModel(),
                // validation aggregator for all sub-components (actions)
                validationAggregator: new ValidationAggregator(),
                actionPreProcessor,
                newActions: [],
                isDefinitionReady: false,
            };
        },
        validations() {
            return {
                item: {
                    name: {required},
                },
            };
        },
        methods: {
            getDelegate(callback) {
                return this.$api(({events}) => callback(events));
            },
            async fetchData() {
                this.isDefinitionReady = false;
                const {eventType} = this;
                const {fields} = await this.getDelegate(type => type.getRules({eventType}));
                this.ruleSet = new RuleSetModel(fields);
                // parse labels again because they might not exist in memory
                await this.$store.dispatch('rulesEngine/parseLabels', fields.rules);
                this.item = RuleFactory.create(
                    this.item.family,
                    this.isFormUpdating ? fields[this.item.family][this.ruleIndex] : {},
                );
                this.isDefinitionReady = true;
            },
            addAction(name) {
                const [action] = RuleModel.mapRuleActions([{name}]);
                this.newActions.push(action.$internalId);
                this.item.actions.push(action);
            },
            toggleActionStatus(index) {
                this.item.actions[index].status = !this.item.actions[index].status;
            },
            deleteAction(index) {
                const [deletedAction] = this.item.actions.splice(index, 1);
                const spliceIndex = this.newActions.findIndex(v => v === deletedAction.$internalId);
                this.newActions.splice(spliceIndex, 1);
            },
            updateAction({action, index}) {
                // allow changes to actions only
                // if the rule isn't already in the process of saving
                if (!this.isLoading) {
                    // TODO block UI interactions instead of having to use this condition
                    this.$set(this.item.actions, index, action);
                }
            },
            updateCriteria(criteria) {
                this.item.criteria = criteria;
            },
            isNewlyAddedAction({$internalId}) {
                return this.newActions.find(v => v === $internalId) !== undefined;
            },
            async save() {
                this.$v.$touch();
                // always trigger aggregated validation
                const actionErrors = this.validationAggregator.validateAggregated();
                if (!this.$v.item.$error && !actionErrors) {
                    if (this.isFormUpdating) {
                        // when updating, reassign at current index within collection
                        this.ruleSet[this.item.family][this.ruleIndex] = this.item;
                    } else {
                        // otherwise push at the end of the collection
                        this.ruleSet[this.item.family].push(this.item);
                    }
                    await this.pushUpdate();
                }
            },
            async pushUpdate() {
                this.isLoading = true;
                try {
                    const filters = {
                        limit: 1,
                        eventType: this.eventType,
                    };
                    const {items} = await this.getDelegate(type => type.getRulesHistory(filters));
                    const [latest = false] = items;
                    // check if the latest event is of the same version
                    if (latest && latest.fields.version !== this.ruleSet.version) {
                        this.$toast.negative(
                            `This ${this.item.type} has been modified since you started editing it. Your modification was discarded and the data was refreshed.`,
                            {delay: 10000},
                        );
                        // TODO cache changes in store and allow restore
                        await this.fetchData();
                        return;
                    }
                    // process any action steps before continuing
                    await this.actionPreProcessor.resolve();
                    const params = {
                        data: this.ruleSet.getPayload(),
                        eventType: this.eventType,
                    };
                    await this.getDelegate(type => type.updateRules(params));
                    this.$router.push(this.previousRoute);
                } catch (error) {
                    this.$toast.fromError(error, `Unable to modify this ${this.item.type} at this time. Please try again later or contact support.`);
                    this.isLoading = false;
                    if (!this.isFormUpdating) {
                        this.ruleSet[this.item.family].pop();
                    }
                }
            },
        },
    };
</script>
